import { useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
export type AdminRole = "admin" | "staff";
export type AdminContext = { role: AdminRole; email: string };
import { useSession, signOut } from "@/lib/auth";
import { api, ApiError } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import AdminActivityToasts from "./AdminActivityToasts";

const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
const tan = colors.tan;

type NavIconName =
  | "dashboard"
  | "posts"
  | "videos"
  | "downloads"
  | "guides"
  | "products"
  | "orders"
  | "coupons"
  | "sales"
  | "retreats"
  | "waitlists"
  | "members"
  | "subscribers"
  | "registrations"
  | "settings"
  | "links"
  | "favorites"
  | "categories"
  | "staff";

type NavLink = { label: string; path: string; ported?: boolean; icon: NavIconName; adminOnly?: boolean };
type NavGroup = { label: string | null; items: NavLink[] };

// `ported` items are React routes; the rest deep-link to the legacy
// server-rendered admin so the panel stays fully usable mid-migration.
const NAV: NavGroup[] = [
  { label: null, items: [{ label: "Dashboard", path: "/admin", ported: true, icon: "dashboard" }] },
  {
    label: "Content",
    items: [
      { label: "Guides", path: "/admin/guides", ported: true, icon: "guides" },
      { label: "Guide Topics", path: "/admin/guide-topics", ported: true, icon: "posts" },
      { label: "Videos", path: "/admin/videos", ported: true, icon: "videos" },
      { label: "Media", path: "/admin/media", ported: true, icon: "downloads" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { label: "Products", path: "/admin/products", ported: true, icon: "products" },
      { label: "Orders", path: "/admin/orders", ported: true, icon: "orders" },
      { label: "Coupons", path: "/admin/coupons", ported: true, icon: "coupons" },
      { label: "Sales", path: "/admin/sales", ported: true, icon: "sales" },
    ],
  },
  {
    label: "Retreats",
    items: [
      { label: "Retreats", path: "/admin/retreats", ported: true, icon: "retreats" },
      { label: "Waitlists", path: "/admin/waitlists", ported: true, icon: "waitlists" },
      { label: "Registrations", path: "/admin/registrations", ported: true, icon: "registrations" },
    ],
  },
  { label: "People", items: [{ label: "Subscribers", path: "/admin/subscribers", ported: true, icon: "subscribers" }] },
  {
    label: "Links",
    items: [
      { label: "Affiliate Links", path: "/admin/affiliate-links", ported: true, icon: "links" },
      { label: "Favorite Links", path: "/admin/favorite-links", ported: true, icon: "favorites" },
      { label: "Categories", path: "/admin/categories", ported: true, icon: "categories" },
    ],
  },
  {
    label: "Settings",
    items: [
      { label: "Settings", path: "/admin/settings", ported: true, icon: "settings" },
      { label: "Staff", path: "/admin/staff", ported: true, icon: "staff", adminOnly: true },
    ],
  },
];

function AdminNavIcon({ name }: { name: NavIconName }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <rect x="2" y="2" width="6" height="6" rx="1.5" />
          <rect x="10" y="2" width="6" height="6" rx="1.5" />
          <rect x="2" y="10" width="6" height="6" rx="1.5" />
          <rect x="10" y="10" width="6" height="6" rx="1.5" />
        </svg>
      );
    case "posts":
      return (
        <svg {...common}>
          <path d="M3 4h12M3 8h9M3 12h6" />
        </svg>
      );
    case "videos":
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="7" />
          <polygon points="7.5,6.5 13,9 7.5,11.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "downloads":
      return (
        <svg {...common}>
          <path d="M9 3v9M5 9l4 4 4-4" />
          <path d="M3 15h12" />
        </svg>
      );
    case "guides":
      return (
        <svg {...common}>
          <path d="M2 13V8a7 7 0 0114 0v5" />
          <path d="M2 13a2 2 0 004 0M12 13a2 2 0 004 0" />
        </svg>
      );
    case "products":
      return (
        <svg {...common}>
          <path d="M3 3h2l2.5 8h7l1.5-5H6" />
          <circle cx="8" cy="15" r="1" />
          <circle cx="14" cy="15" r="1" />
        </svg>
      );
    case "orders":
      return (
        <svg {...common}>
          <rect x="2" y="4" width="14" height="11" rx="1.5" />
          <path d="M6 4V3a1 1 0 012 0v1M10 4V3a1 1 0 012 0v1" />
        </svg>
      );
    case "coupons":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="14" height="8" rx="1.5" />
          <path d="M6 9h6M9 7v4" />
        </svg>
      );
    case "sales":
      return (
        <svg {...common}>
          <polyline points="2,13 6,9 9,12 16,5" />
        </svg>
      );
    case "retreats":
      return (
        <svg {...common}>
          <path d="M2 14l7-9 7 9" />
          <rect x="7" y="10" width="4" height="4" />
        </svg>
      );
    case "waitlists":
      return (
        <svg {...common}>
          <path d="M3 6h12M3 10h8M3 14h5" />
        </svg>
      );
    case "members":
      return (
        <svg {...common}>
          <circle cx="9" cy="6" r="3" />
          <path d="M3 15c0-3.314 2.686-6 6-6s6 2.686 6 6" />
        </svg>
      );
    case "subscribers":
      return (
        <svg {...common}>
          <circle cx="6" cy="7" r="2.5" />
          <path d="M2 15c0-2.485 1.79-4.5 4-4.5" />
          <circle cx="13" cy="7" r="2.5" />
          <path d="M16 15c0-2.485-1.79-4.5-4-4.5" />
          <path d="M9 11c1.657 0 3 1.343 3 3H6c0-1.657 1.343-3 3-3z" />
        </svg>
      );
    case "registrations":
      return (
        <svg {...common}>
          <path d="M3 5h12M3 9h9M3 13h6" />
          <circle cx="14" cy="13" r="3" />
          <path d="M13 13l.8.8 1.5-1.5" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="2.5" />
          <path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.22 3.22l1.42 1.42M13.36 13.36l1.42 1.42M3.22 14.78l1.42-1.42M13.36 4.64l1.42-1.42" />
        </svg>
      );
    case "links":
      return (
        <svg {...common}>
          <path d="M7.5 5.5l1-1a3 3 0 014.24 4.24l-1.4 1.4" />
          <path d="M10.5 12.5l-1 1a3 3 0 01-4.24-4.24l1.4-1.4" />
          <path d="M7 11l4-4" />
        </svg>
      );
    case "favorites":
      return (
        <svg {...common}>
          <path d="M9 15s-6-3.6-6-8a3.2 3.2 0 016-1.6A3.2 3.2 0 0115 7c0 4.4-6 8-6 8z" />
        </svg>
      );
    case "categories":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="5" height="5" rx="1.2" />
          <rect x="10" y="3" width="5" height="5" rx="1.2" />
          <rect x="3" y="10" width="5" height="5" rx="1.2" />
          <path d="M11 12.5h4M13 10.5v4" />
        </svg>
      );
    case "staff":
      return (
        <svg {...common}>
          <circle cx="6.5" cy="6" r="2.5" />
          <path d="M2 15c0-2.5 2-4.5 4.5-4.5S11 12.5 11 15" />
          <circle cx="13" cy="7" r="2" />
          <path d="M12.5 10.6c2 .2 3.5 1.9 3.5 4.4" />
        </svg>
      );
  }
}

function NavItemLink({ item, active }: { item: NavLink; active: boolean }) {
  const className = "aflt-admin-navitem" + (active ? " active" : "");
  const inner = (
    <span className="aflt-admin-navitem-main">
      <AdminNavIcon name={item.icon} />
      <span>{item.label}</span>
    </span>
  );
  if (item.ported) {
    return (
      <Link to={item.path} className={className}>
        {inner}
      </Link>
    );
  }
  // legacy server-rendered page (different origin)
  return (
    <a href={`${API_BASE}${item.path}`} className={className}>
      {inner}
      <span className="legacy-dot" title="classic admin" aria-hidden>
        ↗
      </span>
    </a>
  );
}

export default function AdminShell() {
  const { data: session, isPending } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const [authState, setAuthState] = useState<"checking" | "ok" | "forbidden">("checking");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("staff");

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      nav("/login");
      return;
    }
    api
      .get<{ user: { email: string; role: string } }>("/api/admin/me")
      .then((r) => {
        setEmail(r.user.email);
        setRole(r.user.role === "admin" ? "admin" : "staff");
        setAuthState("ok");
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setAuthState("forbidden");
        else nav("/login");
      });
  }, [session, isPending, nav]);

  if (authState === "checking") {
    return <CenterNote>Checking access…</CenterNote>;
  }
  if (authState === "forbidden") {
    return (
      <CenterNote>
        This account isn&rsquo;t an admin.{" "}
        <Link to="/" style={{ color: colors.berryRose }}>
          Back to site
        </Link>
      </CenterNote>
    );
  }

  const initial = (email.charAt(0) || "A").toUpperCase();

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: colors.cream }}>
      <style>{styles}</style>

      <aside className="aflt-admin-sidebar">
        <div className="aflt-admin-head">
          <div style={{ lineHeight: 1.2 }}>
            <img
              src="/logos/text-logo-purple.png"
              alt="A Few Loose Threads"
              style={{ height: 26, width: "auto", display: "block" }}
              width={500}
              height={185}
            />
            <span
              style={{
                display: "block",
                fontFamily: fonts.body,
                fontSize: 10,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: `${colors.navy}80`,
                marginTop: 3,
              }}
            >
              Admin
            </span>
          </div>
        </div>

        <nav className="aflt-admin-nav">
          {NAV.map((group, gi) => {
            const items = group.items.filter((it) => !it.adminOnly || role === "admin");
            if (items.length === 0) return null;
            return (
              <div key={gi} className={group.label ? "aflt-admin-group" : ""}>
                {group.label && <span className="aflt-admin-grouplabel">{group.label}</span>}
                {items.map((item) => (
                  <NavItemLink
                    key={item.path + item.label}
                    item={item}
                    active={item.ported === true && (item.path === "/admin" ? loc.pathname === "/admin" : loc.pathname.startsWith(item.path))}
                  />
                ))}
              </div>
            );
          })}
        </nav>

        <div className="aflt-admin-foot">
          <div className="aflt-admin-footavatar">{initial}</div>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
            <span className="aflt-admin-footemail">{email || "Admin"}</span>
            <button type="button" className="aflt-admin-signout" onClick={() => signOut().then(() => nav("/login"))}>
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main style={{ flex: 1, minWidth: 0 }}>
        <div style={{ maxWidth: 1040, margin: "0 auto", padding: "44px 48px 64px" }}>
          <Outlet context={{ role, email } satisfies AdminContext} />
        </div>
      </main>
      <AdminActivityToasts />
    </div>
  );
}

function CenterNote({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.cream,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: fonts.body,
        color: `${colors.navy}aa`,
        fontWeight: 600,
        letterSpacing: "0.03em",
        gap: 6,
      }}
    >
      {children}
    </div>
  );
}

const styles = `
  .aflt-admin-sidebar {
    width: 240px; flex-shrink: 0; background: ${colors.cream};
    background-image:
      repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(201,147,76,0.04) 3px, rgba(201,147,76,0.04) 4px),
      repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(201,147,76,0.04) 3px, rgba(201,147,76,0.04) 4px);
    border-right: 2px dashed rgba(201,147,76,0.40);
    display: flex; flex-direction: column;
    position: sticky; top: 0; height: 100vh; overflow: hidden;
  }
  .aflt-admin-head {
    padding: 26px 20px 18px; display: flex; align-items: center; gap: 12px;
    border-bottom: 1px solid rgba(201,147,76,0.15);
  }
  .aflt-admin-nav { flex: 1; overflow-y: auto; padding: 14px 12px; }
  .aflt-admin-navitem {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px; padding: 8px 14px; border-radius: 999px;
    font-family: ${fonts.body}; font-size: 13.5px; font-weight: 600;
    color: rgba(8,45,61,0.80); text-decoration: none; margin-bottom: 2px;
    transition: background 0.15s, color 0.15s; cursor: pointer;
  }
  .aflt-admin-navitem-main {
    min-width: 0; display: inline-flex; align-items: center; gap: 10px;
    white-space: nowrap;
  }
  .aflt-admin-navitem-main svg {
    flex-shrink: 0; opacity: 0.72;
    transition: opacity 0.15s, color 0.15s;
  }
  .aflt-admin-navitem:hover { background: ${colors.blushPink}; color: ${colors.berryRose}; }
  .aflt-admin-navitem.active { background: ${colors.blushPink}; color: ${colors.berryRose}; }
  .aflt-admin-navitem:hover .aflt-admin-navitem-main svg,
  .aflt-admin-navitem.active .aflt-admin-navitem-main svg { opacity: 1; }
  .aflt-admin-navitem .legacy-dot { font-size: 11px; opacity: 0.4; }
  .aflt-admin-group { margin-top: 12px; }
  .aflt-admin-grouplabel {
    font-family: ${fonts.heading}; font-size: 13px; font-weight: 600; color: ${tan};
    text-transform: uppercase; letter-spacing: 0.16em; padding: 4px 14px 5px; display: block;
  }
  .aflt-admin-foot {
    padding: 14px 18px; border-top: 1px dashed rgba(201,147,76,0.25);
    display: flex; align-items: center; gap: 10px;
  }
  .aflt-admin-footavatar {
    width: 34px; height: 34px; border-radius: 50%;
    background: linear-gradient(135deg, ${colors.berryRose} 0%, ${colors.berryPink} 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    font-family: ${fonts.logo}; font-size: 13px; font-weight: 600; color: #fff;
  }
  .aflt-admin-footemail {
    font-family: ${fonts.body}; font-size: 12px; font-weight: 600; color: ${colors.navy};
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px;
  }
  .aflt-admin-signout {
    background: none; border: none; padding: 0; text-align: left;
    font-family: ${fonts.body}; font-size: 11px; font-weight: 600;
    color: rgba(8,45,61,0.47); letter-spacing: 0.05em; cursor: pointer;
  }
  .aflt-admin-signout:hover { color: ${colors.berryRose}; }

  .aflt-admin-title {
    font-family: ${fonts.logo}; font-size: 34px; font-weight: 500; color: ${colors.navy};
    line-height: 1.1; margin: 0;
  }
`;
